import { ApolloClient } from '@apollo/client';
import { EnrollmentDataSource } from '~/typings/context';
import { sendToSegment, trackError } from '~/utils/tracking';
import { UnparsedQueryParams } from '../../types';
import { GET_FUNNEL_QUERY_PARAMS } from './funnel-query-params-token.queries';

interface FunnelQueryParams {
  email: string;
  phone: string;
  zipCode: string;
  lastName: string;
  firstName: string;
  utilityId: number;
  inviteCode: string;
  meterNumber?: string;
  accountNumber: string;
  partnerSource: string;
  serviceAddressZip: string;
  serviceAddressCity: string;
  serviceAddressState: string;
  annualizedUsageEstimate: number;
  preferredEnrollment: EnrollmentDataSource;
  serviceAddressStreetOne: string;
  serviceAddressStreetTwo: string;
  secondaryAccountIdentifier?: string;
}

interface FunnelQueryParamsResponse {
  params: FunnelQueryParams;
  error: string | null;
}

export const handleFunnelQueryParamsToken = async ({
  apolloClient,
  queryParams,
}: {
  apolloClient: ApolloClient<{}>;
  queryParams: UnparsedQueryParams;
}) => {
  const { funnelQueryParamsToken } = queryParams;

  if (!funnelQueryParamsToken) {
    return {
      params: {},
      error: null,
    } as FunnelQueryParamsResponse;
  }

  try {
    const { data } = await apolloClient.query<{
      funnelQueryParams: FunnelQueryParamsResponse;
    }>({
      query: GET_FUNNEL_QUERY_PARAMS,
      variables: {
        token: funnelQueryParamsToken,
      },
    });

    sendToSegment({
      type: 'funnel_query_params_query_complete',
      statusText: 'OK',
      funnelQueryParamsToken,
      error: null,
    });
    return data.funnelQueryParams as FunnelQueryParamsResponse;
  } catch (error) {
    trackError(error, { extras: { funnelQueryParamsToken } });
    const payload = {
      statusText: 'FAIL',
      funnelQueryParamsToken,
      error: error.message || 'Unknown error',
    };
    trackError(error, { extras: { ...payload } });
    sendToSegment({
      type: 'funnel_query_params_query_complete',
      ...payload,
    });
    return {
      params: {},
      error:
        (error as Error).message ||
        'An unknown error occurred while fetching external partner params.',
    } as FunnelQueryParamsResponse;
  }
};
